import { CTABand } from "../../components/site/CTABand";
import { TrustBand } from "../../components/site/TrustBand";
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Layers, Building2, Workflow, ShieldCheck, ChevronDown, Compass, FileSearch, PenTool, PackageCheck, Cable, Blocks, ClipboardCheck, BookOpen, Headset, Wrench, RefreshCcw, Target, Puzzle, Gauge, TrendingUp, Handshake, Network, Camera, Server, PhoneCall, Zap, Cpu, Newspaper, Cctv, KeyRound, MapPin, Fingerprint, FireExtinguisher, BatteryCharging, CalendarCheck, Code2, Wifi, Cloud, Package, MonitorSmartphone, ShoppingCart, Hotel, HeartPulse, GraduationCap, Home as HomeIcon, Building, Briefcase, Check, Users, Lightbulb, Sun } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { Chapter } from "../../components/site/Chapter";
import { CompanyVideo } from "../../components/site/CompanyVideo";
import { HolidayBanner } from "../../components/site/HolidayBanner";
import { HeroGlobe } from "../../components/site/HeroGlobe";
import { Marquee } from "../../components/site/Marquee";
import { NetworkCanvas } from "../../components/NetworkCanvas";
import { KeralaMap } from "../../components/KeralaMap";
import { EnquiryForm, ContactAside } from "../../components/EnquiryForm";
import { Reveal, MaskedLine, FadeIn, Stagger, StaggerItem } from "../../components/motion/Reveal";
import { scrollToId } from "../../lib/lenis";
import { CAPABILITIES, DISTRICTS } from "../../data/content";
import { SOLUTION_DETAILS } from "../../data/solutions";
import { SERVICES } from "../../data/services";
import { INDUSTRY_DETAILS } from "../../data/industries";
import { PROJECT_CATEGORIES } from "../../data/projects";
import { CASE_STUDIES } from "../../data/caseStudies";
import { ARTICLES } from "../../data/articles";
import { TECH_CATEGORIES } from "../../data/technology";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLUE_ACCENT = "59, 130, 246";
const BTN_PRIMARY = "group inline-flex items-center justify-center gap-3 rounded-md bg-nx-blue hover:bg-nx-bluelight text-white font-semibold text-sm tracking-wide px-8 py-3.5 shadow-[0_0_28px_rgba(37,99,235,0.35)] transition-all duration-300 hover:-translate-y-0.5";
const BTN_SECONDARY = "group inline-flex items-center justify-center gap-3 rounded-md border border-white/20 hover:border-nx-bluelight text-white font-semibold text-sm tracking-wide px-8 py-3.5 transition-all duration-300 hover:-translate-y-0.5 hover:bg-nx-blue/10";
const CARD = "bg-[#0b1220] border border-white/10 hover:border-nx-bluelight/60 rounded-lg transition-colors duration-300";
const ICON_BOX = "inline-flex items-center justify-center w-12 h-12 rounded-md bg-nx-blue/10 border border-nx-blue/30 text-nx-bluelight";

const CAP_ICONS = [Code2, Layers, Building2, Workflow, ShieldCheck];
const SERVICE_ICONS = [Code2, Compass, FileSearch, PenTool, PackageCheck, Cable, Blocks, ClipboardCheck, BookOpen, ShieldCheck, Headset, Wrench, RefreshCcw, RefreshCcw, Target, Workflow, Zap];
const TECH_ICONS = [Network, Camera, Server, PhoneCall, Zap, Cpu, Blocks];

const SOLUTION_ICONS = {
  "it-infrastructure": Server, "network-infrastructure": Network, "wifi-wireless": Wifi, "security-surveillance": Cctv,
  "access-control": Fingerprint, "communication": PhoneCall, "smart-office": MonitorSmartphone, "automation": Cpu,
  "power-continuity": BatteryCharging, "cloud-digital-infrastructure": Cloud, "it-products-supply": Package,
};

const INDUSTRY_ICONS = {
  "corporate-offices": Building2, retail: ShoppingCart, hospitality: Hotel, healthcare: HeartPulse,
  education: GraduationCap, residential: HomeIcon, "commercial-buildings": Building, smb: Briefcase,
};

const WHY = [
  { icon: Target, title: "Solution-oriented", text: "Design from your requirement — never from stock to clear." },
  { icon: Puzzle, title: "Integrated", text: "Security, network, communication and IT engineered as one system." },
  { icon: Gauge, title: "Engineering-driven", text: "Surveys, documentation and testing behind every decision." },
  { icon: TrendingUp, title: "Scalable", text: "Infrastructure that grows without being rebuilt." },
  { icon: RefreshCcw, title: "Lifecycle-focused", text: "We don't disappear after installation — AMC and support built in." },
  { icon: Handshake, title: "One technology partner", text: "Consulting to long-term support under one accountable roof." },
];

const PROCESS = [
  { no: "01", title: "Consult & Survey", text: "We listen first, then assess your site anywhere in Kerala — physical, wireless and infrastructure." },
  { no: "02", title: "Design & Engineer", text: "A documented design: architecture, bill of quantities and phasing, aligned to your budget." },
  { no: "03", title: "Deliver & Integrate", text: "Supply, installation, configuration and integration — tested and commissioned before handover." },
  { no: "04", title: "Support & Manage", text: "AMC, managed services and on-site support across all fourteen districts, for the long term." },
];

const HERO_SERVICES = [
  { icon: Code2, label: "Software" },
  { icon: KeyRound, label: "Access Control" },
  { icon: Fingerprint, label: "Biometric Systems" },
  { icon: Network, label: "Networking" },
  { icon: Cctv, label: "CCTV" },
  { icon: FireExtinguisher, label: "Fire Alarm" },
  { icon: BatteryCharging, label: "UPS" },
  { icon: CalendarCheck, label: "Annual Maintenance Contracts" },
];

const PILLARS = [
  { icon: TrendingUp, label: "Stronger Businesses" },
  { icon: Users, label: "Safer People" },
  { icon: Lightbulb, label: "Smarter Infrastructure" },
  { icon: Sun, label: "Brighter Tomorrow" },
];

const STATS = [
  { value: "24/7", label: "Security & Support" },
  { value: "99.9%", label: "Infrastructure Reliability" },
  { value: "14", label: "Districts Across Kerala" },
  { value: "11", label: "Integrated Solution Areas" },
  { value: "A Safer Tomorrow", label: "Our Commitment", small: true },
];

const PRODUCTS = [
  { name: "NexSecure Connect", tagline: "Secure Communication for Modern Teams", image: "/images/home/product-connect.webp", features: ["Secure Chat & Calling", "Custom Business Number", "Spam Detection", "Team & Enterprise Ready"] },
  { name: "NS Chat", tagline: "Private. Secure. Reliable.", image: "/images/home/product-chat.webp", features: ["Group Calls (up to 250 users)", "End-to-End Encryption", "Business & Enterprise Use", "Cross Platform"] },
];

const QuickQuote = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const inputCls = "w-full rounded-md bg-white/[0.05] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-bluelight transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, { type: "request-quote", company: "", ...form });
      setDone(true);
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="relative bg-nx-navy border-b border-white/10 py-14 overflow-hidden" data-testid="home-quick-quote">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(37,99,235,0.12),transparent_55%)]" />
      <div className="relative nx-wrap">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end gap-8">
            <div className="lg:w-80 shrink-0">
              <p className="text-[10px] tracking-[0.28em] uppercase text-nx-bluelight mb-2">Quick Quote</p>
              <h2 className="font-outfit font-semibold text-2xl sm:text-3xl text-white leading-snug">Get a price in 24 hours.</h2>
              {done && <p className="mt-3 text-sm text-nx-bluelight" data-testid="quick-quote-success">Received — our sales team will reach out shortly.</p>}
            </div>
            <form onSubmit={submit} className="flex-1 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="quick-quote-form">
              <input required minLength={1} maxLength={120} placeholder="Full Name *" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quick-quote-name" />
              <input required type="tel" maxLength={16} placeholder="Phone *" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d+\s-]/g, "") })} data-testid="quick-quote-phone" />
              <input required type="email" maxLength={160} placeholder="Email *" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quick-quote-email" />
              <input required minLength={1} maxLength={200} placeholder="Requirement (e.g. 8 CCTV cameras) *" className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="quick-quote-requirement" />
              <button type="submit" disabled={busy} className={`sm:col-span-2 lg:col-span-4 ${BTN_PRIMARY} disabled:opacity-60`} data-testid="quick-quote-submit">
                {busy ? "Sending…" : "Request a Quote"} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Hero = () => {
  const { scrollY } = useScroll();
  const yCanvas = useTransform(scrollY, [0, 900], [0, 160]);
  const fade = useTransform(scrollY, [0, 550], [1, 0.15]);

  return (
    <section id="top" className="relative min-h-svh flex items-start overflow-hidden bg-nx-ink2" data-testid="home-hero">
      <motion.div style={{ y: yCanvas }} className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,rgba(37,99,235,0.22),transparent_55%)]" />
        <NetworkCanvas className="absolute inset-0 w-full h-full" skyline={false} accent={BLUE_ACCENT} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(5,8,15,0.85)_100%)]" />
      </motion.div>
      <motion.div style={{ opacity: fade }} className="relative nx-wrap pt-20 pb-16 sm:pt-24 lg:pt-36 lg:pb-20 w-full">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-8 items-center">
          <div>
            <FadeIn delay={0.15}>
              <div className="flex items-center gap-4 mb-6" data-testid="home-hero-eyebrow">
                <span className="h-px w-10 bg-nx-bluelight/70 shrink-0" />
                <span className="text-[10px] sm:text-xs tracking-[0.28em] uppercase text-nx-bluelight">A Safer Digital Tomorrow</span>
              </div>
            </FadeIn>
            <h1 className="font-outfit font-bold uppercase text-4xl sm:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight" data-testid="home-hero-title">
              <MaskedLine delay={0.3}>Securing the</MaskedLine>
              <MaskedLine delay={0.5} innerClassName="text-nx-bluelight">Digital World</MaskedLine>
            </h1>
            <FadeIn delay={0.75}>
              <p className="mt-5 font-outfit text-lg md:text-2xl text-white/90 font-medium" data-testid="home-hero-subheading">Intelligent Technology. Trusted Security.</p>
            </FadeIn>
            <FadeIn delay={0.85}>
              <p className="mt-4 max-w-xl text-sm md:text-base text-slate-400 leading-relaxed" data-testid="home-hero-description">
                NexSecure Technologies delivers next-generation cybersecurity, networking, infrastructure and digital solutions for organizations that cannot compromise on security.
              </p>
            </FadeIn>
            <FadeIn delay={0.95}>
              <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-4">
                <Link to="/solutions" data-testid="home-hero-cta-explore" className={BTN_PRIMARY}>
                  Explore Solutions <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link to="/contact" data-testid="home-hero-cta-experts" className={BTN_SECONDARY}>
                  Talk to Our Experts
                </Link>
              </div>
            </FadeIn>
            <FadeIn delay={1.05}>
              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4" data-testid="home-hero-pillars">
                {PILLARS.map((p) => {
                  const Icon = p.icon;
                  return (
                    <span key={p.label} className="flex items-center gap-3 text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-slate-300 leading-snug">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-nx-blue/40 text-nx-bluelight shrink-0"><Icon size={15} strokeWidth={1.6} /></span>
                      {p.label}
                    </span>
                  );
                })}
              </div>
            </FadeIn>
            <FadeIn delay={1.15}>
              <p className="mt-8 flex items-center gap-2 text-sm text-slate-400" data-testid="home-hero-kerala">
                <MapPin size={15} className="text-nx-bluelight" /> Serving Businesses Across Kerala
              </p>
            </FadeIn>
          </div>
          <FadeIn delay={0.4} className="relative">
            <div className="relative mx-auto max-w-[540px] aspect-square" data-testid="home-hero-visual">
              <div className="absolute inset-8 rounded-full bg-nx-blue/25 blur-3xl" />
              <HeroGlobe className="absolute inset-0" />
              <div className="absolute bottom-[30%] right-[8%] text-nx-bluelight drop-shadow-[0_0_24px_rgba(59,130,246,0.9)]" data-testid="home-hero-shield">
                <ShieldCheck size={96} strokeWidth={1.2} className="w-16 h-16 sm:w-24 sm:h-24" />
              </div>
              <div className="absolute top-3 right-2 sm:right-0 text-right text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-slate-300 leading-[1.9]" data-testid="home-hero-tagline-top">
                Secure<br />Connect<br />Protect<br />Grow
              </div>
              <div className="absolute bottom-6 right-2 sm:right-0 text-right font-outfit font-semibold text-sm sm:text-lg uppercase tracking-wide leading-snug" data-testid="home-hero-tagline-bottom">
                <span className="block text-white">Technology</span>
                <span className="block text-white">Without Limits</span>
                <span className="block text-nx-bluelight">Security</span>
                <span className="block text-nx-bluelight">Without Compromise</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </motion.div>
      <button onClick={() => scrollToId("home-quick-strip")} className="absolute bottom-6 left-1/2 -translate-x-1/2 text-slate-500 hover:text-nx-bluelight transition-colors" aria-label="Scroll down" data-testid="home-scroll-cue">
        <ChevronDown size={20} className="animate-bounce" />
      </button>
    </section>
  );
};

const QuickStrip = () => (
  <section id="home-quick-strip" className="bg-nx-navy border-y border-white/10 py-10" data-testid="home-hero-services">
    <div className="nx-wrap flex flex-col lg:flex-row lg:items-center gap-8">
      <Reveal className="lg:w-72 shrink-0">
        <h2 className="font-outfit font-semibold text-2xl sm:text-3xl text-white leading-snug">Technology built for organizations that demand more.</h2>
      </Reveal>
      <Stagger className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {HERO_SERVICES.map((s) => {
          const Icon = s.icon;
          return (
            <StaggerItem key={s.label}>
              <div className={`${CARD} flex flex-col items-center justify-center text-center gap-3 px-3 py-5 h-full`} data-testid={`home-hero-service-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <Icon size={26} className="text-nx-bluelight" strokeWidth={1.5} />
                <span className="text-xs sm:text-sm text-white font-medium leading-snug">{s.label}</span>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  </section>
);

const StatsBar = () => (
  <section className="bg-nx-ink2 border-b border-white/10" data-testid="home-stats">
    <Stagger className="nx-wrap grid grid-cols-2 lg:grid-cols-5 divide-y sm:divide-y-0 divide-white/10">
      {STATS.map((s, i) => (
        <StaggerItem key={s.label}>
          <div className={`flex flex-col items-center text-center py-9 px-4 ${i > 0 ? "lg:border-l lg:border-white/10" : ""}`} data-testid={`home-stat-${i}`}>
            <span className={`font-outfit font-bold text-nx-bluelight leading-none ${s.small ? "text-xl sm:text-2xl" : "text-4xl sm:text-5xl"}`}>{s.value}</span>
            <span className="mt-3 text-xs sm:text-sm text-slate-300">{s.label}</span>
          </div>
        </StaggerItem>
      ))}
    </Stagger>
  </section>
);

const Capabilities = () => (
  <section id="chapter-capabilities" className="bg-nx-navy border-y border-white/10 py-20 sm:py-28" data-testid="home-capabilities">
    <div className="nx-wrap">
      <Chapter no="01" eyebrow="Core Capabilities" title="One partner, end to end." text="Security, networking, communication, automation and IT — engineered to work as one system, from design to long-term support." className="mb-14" />
      <Stagger className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {CAPABILITIES.map((c, i) => {
          const Icon = CAP_ICONS[i];
          return (
            <StaggerItem key={c.title}>
              <div className={`group h-full ${CARD} p-7`} data-testid={`home-capability-${i}`}>
                <span className={ICON_BOX}><Icon size={22} strokeWidth={1.5} /></span>
                <h3 className="font-outfit font-semibold text-lg text-white mt-6 mb-3">{c.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{c.text}</p>
                <span className="block mt-6 h-px w-8 bg-nx-bluelight/60 group-hover:w-full transition-all duration-500" />
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
      <FadeIn>
        <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 justify-center rounded-lg border border-white/10 bg-white/[0.02] px-6 py-4" data-testid="home-delivery-model">
          {["Supply", "Installation", "Integration", "Configuration", "Support", "AMC"].map((s, i) => (
            <span key={s} className="text-[10px] sm:text-[11px] tracking-[0.22em] uppercase text-slate-300">
              {i > 0 && <span className="text-nx-bluelight mx-3">•</span>}{s}
            </span>
          ))}
        </div>
      </FadeIn>
    </div>
  </section>
);

const Solutions = () => (
  <section className="bg-nx-ink2 py-20 sm:py-28" data-testid="home-solutions">
    <div className="nx-wrap">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
        <Chapter no="02" eyebrow="Our Solutions" title="End-to-end technology for a secure tomorrow." text="Every system we design — from surveillance to structured cabling — is engineered to work as part of a single, manageable whole." />
        <Reveal delay={0.1} className="shrink-0">
          <Link to="/solutions" data-testid="home-solutions-more" className="group inline-flex items-center gap-3 rounded-md border border-nx-blue/50 hover:bg-nx-blue/10 text-nx-bluelight text-sm font-medium px-6 py-3 transition-colors">
            View All Solutions <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {SOLUTION_DETAILS.map((s, i) => {
          const Icon = SOLUTION_ICONS[s.slug] || Blocks;
          return (
            <StaggerItem key={s.slug}>
              <Link to={`/solutions/${s.slug}`} className={`group flex flex-col h-full ${CARD} p-7`} data-testid={`home-solution-${i}`}>
                <div className="flex items-start justify-between">
                  <span className={ICON_BOX}><Icon size={24} strokeWidth={1.5} /></span>
                  <span className="font-outfit text-xs text-slate-500">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-outfit font-semibold text-lg text-white mt-6 leading-snug">{s.name}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed flex-1">{s.tagline}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs font-medium text-nx-bluelight">Learn More <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" /></span>
              </Link>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  </section>
);

const ServicesOverview = () => (
  <section className="bg-nx-navy border-y border-white/10 py-20 sm:py-28" data-testid="home-services">
    <div className="nx-wrap">
      <Chapter no="03" eyebrow="Services" title="We don't disappear after installation." text="Sixteen services across one lifecycle: consult, survey, design, supply, install, integrate, commission, support, manage." className="mb-14" />
      <Stagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-3">
        {SERVICES.slice(0, 9).map((s, i) => {
          const Icon = SERVICE_ICONS[i] || Compass;
          return (
            <StaggerItem key={s.slug}>
              <Link to={`/services/${s.slug}`} className={`group flex flex-col items-start h-full ${CARD} p-5`} data-testid={`home-service-${i}`}>
                <Icon size={20} className="text-nx-bluelight mb-4" strokeWidth={1.5} />
                <span className="text-xs sm:text-sm text-slate-300 group-hover:text-white leading-snug transition-colors">{s.name}</span>
              </Link>
            </StaggerItem>
          );
        })}
      </Stagger>
      <Reveal delay={0.1} className="mt-10">
        <Link to="/services" data-testid="home-services-more" className="group inline-flex items-center gap-3 text-nx-bluelight text-xs tracking-[0.22em] uppercase">
          Explore the full service lifecycle <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const Products = () => (
  <section className="relative bg-nx-ink2 py-20 sm:py-28 overflow-hidden" data-testid="home-products">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(37,99,235,0.14),transparent_55%)]" />
    <div className="relative nx-wrap">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
        <Chapter no="04" eyebrow="Our Products" title="Built by NexSecure for a connected world." text="Innovative products designed to make communication, collaboration and security simpler and stronger." />
        <Reveal delay={0.2} className="shrink-0">
          <span className="inline-block rounded-full border border-nx-blue/40 bg-nx-blue/10 text-nx-bluelight text-[10px] tracking-[0.24em] uppercase px-4 py-1.5" data-testid="home-products-preview-tag">Coming Soon</span>
        </Reveal>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {PRODUCTS.map((p, i) => (
          <Reveal key={p.name} delay={0.1 + i * 0.1}>
            <div className={`${CARD} flex flex-col sm:flex-row gap-6 p-7 h-full`} data-testid={`home-product-${i}`}>
              <img src={p.image} alt={`${p.name} preview`} className="w-full sm:w-52 h-52 sm:h-auto object-cover rounded-md border border-white/10" loading="lazy" />
              <div className="flex-1 flex flex-col">
                <h3 className="font-outfit font-semibold text-xl text-nx-bluelight">{p.name}</h3>
                <p className="text-sm text-slate-300 mt-1">{p.tagline}</p>
                <ul className="mt-4 flex flex-col gap-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-slate-400"><Check size={14} className="text-nx-bluelight shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Link to="/contact" className="group mt-6 inline-flex items-center gap-2 self-start rounded-md border border-nx-blue/50 hover:bg-nx-blue/10 text-nx-bluelight text-sm font-medium px-5 py-2.5 transition-colors" data-testid={`home-product-${i}-cta`}>
                  Learn More <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Industries = () => (
  <section className="bg-nx-navy border-y border-white/10 py-20 sm:py-28" data-testid="home-industries">
    <div className="nx-wrap">
      <Chapter no="05" eyebrow="Industries We Serve" title="Trusted across industries." text="Offices, hospitals, hotels, campuses, factories and communities — each with its own demands, each engineered for specifically." className="mb-14" />
      <Stagger className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {INDUSTRY_DETAILS.map((ind, i) => {
          const Icon = INDUSTRY_ICONS[ind.slug] || Building2;
          return (
            <StaggerItem key={ind.slug}>
              <Link to={`/industries/${ind.slug}`} className={`group flex flex-col items-center text-center h-full ${CARD} px-3 py-7`} data-testid={`home-industry-${i}`}>
                <Icon size={30} className="text-nx-bluelight transition-transform duration-300 group-hover:-translate-y-1" strokeWidth={1.5} />
                <span className="mt-4 text-xs sm:text-sm text-white font-medium leading-snug">{ind.name}</span>
                <ArrowUpRight size={14} className="mt-3 text-slate-600 group-hover:text-nx-bluelight transition-colors" />
              </Link>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  </section>
);

const WhyNexSecure = () => (
  <section className="bg-nx-ink2 py-20 sm:py-28" data-testid="home-why">
    <div className="nx-wrap">
      <Chapter no="06" eyebrow="Why NexSecure" title="Chosen for how we work, not what we claim." className="mb-14" />
      <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {WHY.map((w, i) => {
          const Icon = w.icon;
          return (
            <StaggerItem key={w.title}>
              <div className={`h-full ${CARD} p-7`} data-testid={`home-why-${i}`}>
                <span className={ICON_BOX}><Icon size={22} strokeWidth={1.5} /></span>
                <h3 className="font-outfit font-semibold text-lg text-white mt-6 mb-3">{w.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{w.text}</p>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  </section>
);

const TechnologyBand = () => (
  <section className="bg-nx-navy border-y border-white/10 py-20 sm:py-28" data-testid="home-technology">
    <div className="nx-wrap">
      <Chapter no="07" eyebrow="Technology Ecosystem" title="We engineer the solution. Technology follows." text="Requirements first — then the right manufacturers for performance, reliability, lifecycle and cost." className="mb-14" />
      <Stagger className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {TECH_CATEGORIES.map((c, i) => {
          const Icon = TECH_ICONS[i] || Blocks;
          return (
            <StaggerItem key={c.name}>
              <div className={`flex flex-col items-start h-full ${CARD} p-5`} data-testid={`home-tech-${i}`}>
                <Icon size={20} className="text-nx-bluelight mb-4" strokeWidth={1.5} />
                <span className="text-xs text-slate-300 leading-snug">{c.name}</span>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
      <Reveal delay={0.1} className="mt-10">
        <Link to="/technology" data-testid="home-technology-more" className="group inline-flex items-center gap-3 text-nx-bluelight text-xs tracking-[0.22em] uppercase">
          Our technology philosophy <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const ImageCard = ({ to, image, alt, category, title, subtitle, description, footer, testid }) => {
  const Wrapper = to ? Link : "div";
  return (
    <Wrapper to={to} className={`group flex flex-col h-full ${CARD} overflow-hidden`} data-testid={testid}>
      <div className="relative overflow-hidden">
        <img src={image} alt={alt} className="w-full h-44 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1220] via-transparent to-transparent" />
        <span className="absolute top-4 left-4 rounded-md text-[10px] tracking-[0.22em] uppercase bg-nx-blue text-white px-3 py-1.5">{category}</span>
      </div>
      <div className="flex flex-col flex-1 p-6">
        <h3 className="font-outfit font-semibold text-lg text-white leading-snug">{title}</h3>
        {subtitle && <p className="mt-2 flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-nx-bluelight"><MapPin size={12} /> {subtitle}</p>}
        {description && <p className="text-sm text-slate-400 mt-3 leading-relaxed flex-1">{description}</p>}
        {footer && <span className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-nx-bluelight">{footer} <ArrowUpRight size={13} /></span>}
      </div>
    </Wrapper>
  );
};

const FeaturedProjects = () => (
  <section className="bg-nx-ink2 py-20 sm:py-28" data-testid="home-projects">
    <div className="nx-wrap">
      <Chapter no="08" eyebrow="Featured Projects" title="Work, documented." text="Requirement, challenge, design, implementation, result — every case study follows the same honest structure." className="mb-14" />
      <Stagger className="grid md:grid-cols-3 gap-6">
        {CASE_STUDIES.map((c) => (
          <StaggerItem key={c.slug}>
            <ImageCard to={`/projects/${c.slug}`} image={c.image} alt={c.title} category={c.category} title={c.title} description={c.location} footer="Case study" testid={`home-project-${c.slug}`} />
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const InstallationsPreview = () => {
  const preview = [
    { ...PROJECT_CATEGORIES[0].projects[0], category: PROJECT_CATEGORIES[0].name, image: PROJECT_CATEGORIES[0].image, slug: "office" },
    { ...PROJECT_CATEGORIES[2].projects[0], category: PROJECT_CATEGORIES[2].name, image: PROJECT_CATEGORIES[2].image, slug: "cctv" },
    { ...PROJECT_CATEGORIES[4].projects[0], category: PROJECT_CATEGORIES[4].name, image: PROJECT_CATEGORIES[4].image, slug: "networking" },
  ];
  return (
    <section className="bg-nx-navy border-y border-white/10 py-20 sm:py-28" data-testid="home-installations">
      <div className="nx-wrap">
        <Chapter no="09" eyebrow="Installations" title="Completed installations across Kerala." text="Offices, apartments, campuses and factories — a preview of recent work by category." className="mb-14" />
        <Stagger className="grid md:grid-cols-3 gap-6">
          {preview.map((p) => (
            <StaggerItem key={p.title}>
              <ImageCard image={p.image} alt={p.title} category={p.category} title={p.title} subtitle={p.location} description={p.description} testid={`home-installation-${p.slug}`} />
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal delay={0.15} className="mt-12">
          <Link to="/projects" data-testid="home-projects-view-all" className={BTN_PRIMARY}>
            View All Projects <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const AmcBand = () => (
  <section className="relative bg-nx-ink2 py-20 sm:py-24 overflow-hidden" data-testid="home-amc">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(37,99,235,0.14),transparent_55%)]" />
    <div className="relative nx-wrap flex flex-col lg:flex-row lg:items-center gap-10 justify-between">
      <div className="max-w-xl">
        <Reveal>
          <span className="text-[11px] tracking-[0.3em] uppercase text-nx-bluelight">Managed Services & AMC</span>
          <h2 className="font-outfit font-semibold text-3xl sm:text-4xl text-white mt-4">Infrastructure that stays healthy — on schedule, not on luck.</h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-4 text-base text-slate-400 leading-relaxed">Preventive maintenance, priority breakdown response, health reports and lifecycle planning across IT, network, CCTV, access control, Wi-Fi and EPABX systems.</p>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="flex flex-col sm:flex-row lg:flex-col gap-4 shrink-0">
        <Link to="/services/amc" data-testid="home-amc-link" className={BTN_PRIMARY}>
          About AMC <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
        <Link to="/services/managed-services" data-testid="home-managed-link" className={BTN_SECONDARY}>Managed IT Services</Link>
      </Reveal>
    </div>
  </section>
);

const Coverage = () => (
  <section className="bg-nx-navy border-y border-white/10 py-20 sm:py-28" data-testid="home-coverage">
    <div className="nx-wrap grid lg:grid-cols-2 gap-14 items-center">
      <Reveal className="relative flex justify-center order-2 lg:order-1">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.14),transparent_65%)]" />
        <KeralaMap tone="blue" className="relative h-[380px] sm:h-[460px] w-auto" />
      </Reveal>
      <div className="order-1 lg:order-2">
        <Chapter no="10" eyebrow="Our Presence" title="Expanding possibilities across Kerala." text="Serving businesses, institutions, industries, government organisations and communities across all fourteen districts — from our base in Ernakulam to every corner of the state." />
        <Reveal delay={0.2}>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="home-district-list">
            {DISTRICTS.map((d) => (
              <span key={d} className="rounded-md text-[11px] tracking-wider uppercase text-slate-300 border border-white/15 px-3 py-1.5 hover:border-nx-bluelight/60 hover:text-nx-bluelight transition-colors">{d}</span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" data-testid="home-coverage-primary-cta" className={BTN_PRIMARY}>
              Talk to Our Team <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/solutions" data-testid="home-coverage-secondary-cta" className={BTN_SECONDARY}>Explore Solutions</Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="bg-nx-ink2 py-20 sm:py-28" data-testid="home-process">
    <div className="nx-wrap">
      <Chapter no="11" eyebrow="How We Work" title="A process built on accountability." className="mb-14" />
      <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PROCESS.map((p) => (
          <StaggerItem key={p.no}>
            <div className={`h-full ${CARD} p-7`} data-testid={`home-process-${p.no}`}>
              <span className="font-outfit font-bold text-4xl text-nx-bluelight/80">{p.no}</span>
              <h3 className="font-outfit font-semibold text-lg text-white mt-6 mb-3">{p.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{p.text}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const InsightsPreview = () => (
  <section className="bg-nx-navy border-y border-white/10 py-20 sm:py-28" data-testid="home-insights">
    <div className="nx-wrap">
      <Chapter no="12" eyebrow="Insights" title="Practical knowledge, from the field." className="mb-14" />
      <Stagger className="grid md:grid-cols-3 gap-6">
        {ARTICLES.slice(0, 3).map((a) => (
          <StaggerItem key={a.slug}>
            <Link to={`/insights/${a.slug}`} className={`group flex flex-col h-full ${CARD} p-7`} data-testid={`home-insight-${a.slug}`}>
              <Newspaper size={22} className="text-nx-bluelight mb-5" strokeWidth={1.5} />
              <span className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">{a.category} · {a.readTime}</span>
              <h3 className="font-outfit font-semibold text-lg text-white leading-snug flex-1">{a.title}</h3>
              <span className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-nx-bluelight">Read <ArrowUpRight size={13} /></span>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const CareersBand = () => (
  <section className="relative bg-nx-ink2 overflow-hidden" data-testid="home-careers">
    <img src="/images/hero-city-night.webp" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-r from-nx-ink2 via-nx-ink2/90 to-nx-ink2/40" />
    <div className="relative nx-wrap py-20 sm:py-24 grid lg:grid-cols-[1fr_1fr_auto] gap-10 items-center">
      <Reveal>
        <span className="text-[11px] tracking-[0.3em] uppercase text-nx-bluelight">Careers</span>
        <h2 className="font-outfit font-semibold text-3xl sm:text-4xl text-white mt-4 leading-tight">Build the future of secure technology.</h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="text-base text-slate-300 leading-relaxed">Join a team that is passionate about innovation, security and making a real impact.</p>
        <Link to="/careers" data-testid="home-careers-cta" className={`${BTN_PRIMARY} mt-6`}>
          Join NexSecure <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <Reveal delay={0.2} className="hidden lg:block">
        <p className="font-outfit font-semibold text-sm tracking-[0.3em] uppercase text-white/80 leading-[2.1] border-l border-nx-blue/50 pl-6">Great People<br />Build<br />Extraordinary<br />Things</p>
      </Reveal>
    </div>
  </section>
);

const Enquiry = () => (
  <section id="enquiry" className="bg-nx-navy border-t border-white/10 py-20 sm:py-28" data-testid="home-enquiry">
    <div className="nx-wrap grid lg:grid-cols-[1.6fr_1fr] gap-14">
      <div>
        <Chapter no="13" eyebrow="Start a Conversation" title="Request a site assessment." text="Tell us your solution area and a few lines about your project. Our team will take it from there — no long questionnaires." className="mb-10" />
        <Reveal delay={0.2}>
          <EnquiryForm tone="dark" source="home" buttonLabel="Request a Site Assessment" />
        </Reveal>
      </div>
      <div className="lg:pt-28">
        <Reveal delay={0.25}>
          <div className={`${CARD} p-8`}>
            <p className="text-[10px] tracking-[0.28em] uppercase text-slate-400 mb-6">Prefer to reach us directly?</p>
            <ContactAside tone="dark" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div className="nx-theme-blue font-publicsans bg-nx-ink2 text-slate-200 antialiased" data-testid="home-page">
      <SiteHeader />
      <HolidayBanner />
      <Hero />
      <QuickStrip />
      <StatsBar />
      <CompanyVideo />
      <QuickQuote />
      <Marquee />
      <Capabilities />
      <Solutions />
      <ServicesOverview />
      <Products />
      <TrustBand />
      <Industries />
      <WhyNexSecure />
      <TechnologyBand />
      <FeaturedProjects />
      <InstallationsPreview />
      <AmcBand />
      <Coverage />
      <Process />
      <InsightsPreview />
      <CareersBand />
      <Enquiry />
      <CTABand />
      <SiteFooter />
    </div>
  );
}
